// Privacy Policy Page - Politique de Confidentialité RGPD
import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, Mail, Lock, Eye, Download, Trash2, Clock, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  const { isDarkMode } = useTheme();
  
  const sections = [
    {
      icon: Shield,
      title: "1. Responsable du traitement",
      content: `drbooking S.à r.l.
Adresse : Luxembourg
Email : privacy@drbooking.pro
Délégué à la Protection des Données (DPO) : dpo@drbooking.pro`
    },
    {
      icon: Eye,
      title: "2. Données collectées",
      content: `Nous collectons les données suivantes :

• **Données d'identification** : nom, prénom, email, téléphone
• **Données de santé** : uniquement les informations nécessaires à la prise de rendez-vous (spécialité recherchée, motif de consultation)
• **Données de connexion** : adresse IP, logs de connexion, appareil utilisé
• **Données de navigation** : pages visitées, actions effectuées (avec votre consentement)

**Nous ne collectons PAS** :
• Vos dossiers médicaux complets
• Vos ordonnances
• Vos résultats d'examens`
    },
    {
      icon: Lock,
      title: "3. Finalités du traitement",
      content: `Vos données sont utilisées pour :

• Créer et gérer votre compte utilisateur
• Permettre la prise de rendez-vous
• Envoyer des rappels et notifications
• Améliorer nos services (statistiques anonymisées)
• Assurer la sécurité de la plateforme
• Respecter nos obligations légales`
    },
    {
      icon: Clock,
      title: "4. Durée de conservation",
      content: `• **Données de compte** : conservées tant que le compte est actif, puis 3 ans après suppression
• **Données de rendez-vous** : 5 ans (obligation légale santé)
• **Logs de connexion** : 1 an
• **Cookies** : 13 mois maximum`
    },
    {
      icon: Globe,
      title: "5. Partage des données",
      content: `Vos données peuvent être partagées avec :

• Les professionnels de santé avec lesquels vous prenez rendez-vous
• Nos sous-traitants techniques (hébergement, email) - tous conformes RGPD
• Les autorités en cas d'obligation légale

**Nous ne vendons JAMAIS vos données à des tiers.**`
    },
    {
      icon: Download,
      title: "6. Vos droits RGPD",
      content: `Conformément au RGPD, vous disposez des droits suivants :

• **Droit d'accès** : obtenir une copie de vos données
• **Droit de rectification** : corriger vos données inexactes
• **Droit à l'effacement** : demander la suppression de vos données
• **Droit à la portabilité** : recevoir vos données dans un format standard
• **Droit d'opposition** : vous opposer à certains traitements
• **Droit de limitation** : limiter l'utilisation de vos données

Pour exercer ces droits : privacy@drbooking.pro`
    }
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Politique de Confidentialité
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Dernière mise à jour : Mars 2026
          </p>
        </div>

        {/* Introduction */}
        <div className={`p-6 rounded-xl mb-8 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
          <p className={`leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
            Chez <strong>drbooking</strong>, la protection de vos données personnelles est une priorité absolue. 
            Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations, 
            conformément au <strong>Règlement Général sur la Protection des Données (RGPD)</strong> et à la loi française Informatique et Libertés.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, idx) => (
            <div 
              key={idx}
              className={`p-6 rounded-xl ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.title}
                </h2>
              </div>
              <div className={`whitespace-pre-line leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className={`mt-8 p-6 rounded-xl text-center ${isDarkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
          <Mail className="w-8 h-8 text-blue-500 mx-auto mb-3" />
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Questions sur vos données ?
          </h3>
          <p className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
            Contactez notre DPO : <a href="mailto:dpo@drbooking.pro" className="text-blue-500 hover:underline">dpo@drbooking.pro</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
